export const DEFAULT_PRICE_REPRESENTATION = '—';
const ROUNDING_PRICE_SUFFIX = '–';

export interface HybrisPriceRepresentation {
	value : number | string;
	currencyIso? : string;
}

type PriceType = number | string | HybrisPriceRepresentation | undefined | null;
export type PriceArg = Price | PriceType;

export class Price {
	private price : PriceType;

	constructor(price : PriceArg) {
		if(price instanceof Price) {
			price = {value: price.value(), currencyIso: price.currency()};
		}
		this.price = price;
	}

	public value() : number {
		let value = this.price;
		if(value && typeof value === 'object') {
			value = value.value;
		}
		if(!value && value !== 0) {
			value = undefined;
		}

		return Number(value);
	}

	public isValid() : boolean {
		return this.value() > -1 && this.value() < Infinity;
	}

	/**
	 * @method formatted
	 * @param defaultValue value which will be returned for invalid price
	 * @param withStar optional param used to describe format of cent value suffix '–' or '–*'
	 * @return string
	 */
	public formatted(defaultValue : string = DEFAULT_PRICE_REPRESENTATION, withStar? : boolean) : string {
		if(!this.isValid()) {
			return defaultValue;
		}

		// Calculate in cents (whole integers) so the division by 5 does not cause rounding issues.
		let value = Math.round(this.value() * 100);
		// Round to 0.05, see https://jira.swisscom.com/browse/ECOMX-1502
		value = Math.round(Math.floor(value / 5) * 5);
		// Convert back to whole francs.
		value = value / 100;

		const priceParts = String(value || 0).split('.').slice(0, 2);
		priceParts[1] = (priceParts[1] || '').substr(0, 2);

		if(parseInt(priceParts[1] || '0', 10) === 0) {
			if(withStar) {
				priceParts[1] = ROUNDING_PRICE_SUFFIX + '*';
			} else {
				priceParts[1] = ROUNDING_PRICE_SUFFIX;
			}
		} else if (priceParts[1].length === 1) {
			priceParts[1] += '0';
		}

		return priceParts.join('.');
	}

	public currency() : string {
		if(this.price && typeof this.price === 'object') {
			return this.price.currencyIso || 'CHF';
		}

		return 'CHF';
	}

	// Allow usage in number contexts (calculations and such)
	public valueOf() {
		return this.value();
	}

	// Allow usage in string contexts (replacing, etc.)
	public toString() {
		return this.formatted();
	}

	// tslint:disable-next-line:strict-type-predicates
	public [typeof Symbol !== 'undefined' && Symbol.toPrimitive || 'toPrimitive'](hint : 'default' | 'number' | 'string') {
		switch(hint) {
			case 'string':
				return this.formatted();
			default:
			case 'default':
			case 'number':
				return this.value();
		}
	}
}

export default function makePrice(price? : PriceArg) {
	if(price instanceof Price) {
		return price;
	}

	return new Price(price);
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Price.ts